import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Progress } from '@/shared/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import {
  FileText, Upload, Download, Search, Filter, Plus, RefreshCw, Trash2,
  Eye, Share2, Tag, FolderOpen, Clock, User, Building2, Archive,
  File, FileImage, FileSpreadsheet, AlertTriangle, CheckCircle,
  Link2, Lock, Unlock, Copy, ExternalLink, History, Settings,
  Cloud, HardDrive, Database, X, ChevronRight, MoreVertical } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE || '';
const getEzerpToken = () => localStorage.getItem('ezerpToken');

// Document categories with icons and colors
const CATEGORIES = {
  invoice: { label: 'Invoice', icon: FileText, color: 'text-blue-500 bg-blue-500/10' },
  packing_slip: { label: 'Packing Slip', icon: File, color: 'text-violet-500 bg-violet-500/10' },
  delivery_receipt: { label: 'Delivery Receipt', icon: CheckCircle, color: 'text-emerald-500 bg-emerald-500/10' },
  bill_of_lading: { label: 'Bill of Lading', icon: FileText, color: 'text-amber-500 bg-amber-500/10' },
  proof_of_delivery: { label: 'Proof of Delivery', icon: CheckCircle, color: 'text-green-500 bg-green-500/10' },
  purchase_order: { label: 'Purchase Order', icon: FileText, color: 'text-indigo-500 bg-indigo-500/10' },
  sales_order: { label: 'Sales Order', icon: FileText, color: 'text-cyan-500 bg-cyan-500/10' },
  compliance: { label: 'Compliance', icon: AlertTriangle, color: 'text-red-500 bg-red-500/10' },
  fiscal: { label: 'Fiscal (NF-e/CT-e)', icon: FileSpreadsheet, color: 'text-orange-500 bg-orange-500/10' },
  contract: { label: 'Contract', icon: FileText, color: 'text-slate-500 bg-slate-500/10' },
  return_authorization: { label: 'Return Auth', icon: Archive, color: 'text-pink-500 bg-pink-500/10' },
  shipping_label: { label: 'Shipping Label', icon: Tag, color: 'text-teal-500 bg-teal-500/10' },
  customs: { label: 'Customs', icon: Building2, color: 'text-purple-500 bg-purple-500/10' },
  insurance: { label: 'Insurance', icon: Lock, color: 'text-yellow-500 bg-yellow-500/10' },
  quality_certificate: { label: 'Quality Certificate', icon: CheckCircle, color: 'text-lime-500 bg-lime-500/10' },
  other: { label: 'Other', icon: File, color: 'text-gray-500 bg-gray-500/10' }
};

// Status configurations
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-400' },
  active: { label: 'Active', color: 'bg-emerald-500' },
  archived: { label: 'Archived', color: 'bg-gray-500' },
  expired: { label: 'Expired', color: 'bg-red-500' },
  superseded: { label: 'Superseded', color: 'bg-amber-500' }
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Document Card Component
const DocumentCard = ({ doc, onView, onDownload, onShare, onArchive }) => {const { t } = useTranslation("wms");
  const category = CATEGORIES[doc.category] || CATEGORIES.other;
  const CategoryIcon = category.icon;
  const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.active;

  // Determine file icon based on mime type
  const renderFileIcon = () => {
    const mimeType = doc.mime_type;
    const fileName = doc.file_name;
    if (mimeType?.includes('pdf') || fileName?.endsWith('.pdf')) {
      return <FileText className="h-3 w-3 text-red-500" />;
    }
    if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].some((ext) => fileName?.toLowerCase().endsWith(ext))) {
      return <FileImage className="h-3 w-3" />;
    }
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') || ['xls', 'xlsx', 'csv'].some((ext) => fileName?.toLowerCase().endsWith(ext))) {
      return <FileSpreadsheet className="h-3 w-3" />;
    }
    return <File className="h-3 w-3" />;
  };

  return (
    <Card className="group hover:shadow-lg transition-all cursor-pointer" onClick={() => onView(doc)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center shrink-0', category.color)}>
            <CategoryIcon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-sm truncate">{doc.title}</h3>
                <p className="text-xs text-muted-foreground truncate">{doc.document_id}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => {e.stopPropagation();onView(doc);}}>
                    <Eye className="h-4 w-4 mr-2" />{t("viewDetails", "View Details")}
                  </DropdownMenuItem>
                  {doc.file_path &&
                  <DropdownMenuItem onClick={(e) => {e.stopPropagation();onDownload(doc);}}>
                      <Download className="h-4 w-4 mr-2" />{t("download", "Download")}
                  </DropdownMenuItem>
                  }
                  <DropdownMenuItem onClick={(e) => {e.stopPropagation();onShare(doc);}}>
                    <Share2 className="h-4 w-4 mr-2" />{t("share", "Share")}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {e.stopPropagation();onArchive(doc);}}
                    className="text-red-500">
                    
                    <Archive className="h-4 w-4 mr-2" />{t("archive", "Archive")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="secondary" className="text-xs">
                {category.label}
              </Badge>
              <Badge variant="outline" className="text-xs">
                <span className={cn('w-1.5 h-1.5 rounded-full mr-1', status.color)} />
                {status.label}
              </Badge>
              {doc.version > 1 &&
              <Badge variant="outline" className="text-xs">
                  v{doc.version}
                </Badge>
              }
            </div>

            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              {doc.file_name &&
              <div className="flex items-center gap-1">
                  {renderFileIcon()}
                  <span className="truncate max-w-[100px]">{doc.file_name}</span>
                </div>
              }
              {doc.file_size &&
              <span>{formatFileSize(doc.file_size)}</span>
              }
              <div className="flex items-center gap-1 ml-auto">
                <Clock className="h-3 w-3" />
                {new Date(doc.created_at).toLocaleDateString()}
              </div>
            </div>

            {doc.tags && doc.tags.length > 0 &&
            <div className="flex items-center gap-1 mt-2">
                <Tag className="h-3 w-3 text-muted-foreground" />
                {doc.tags.slice(0, 3).map((tag, i) =>
              <Badge key={i} variant="outline" className="text-xs px-1.5 py-0">
                    {tag}
                  </Badge>
              )}
                {doc.tags.length > 3 &&
              <span className="text-xs text-muted-foreground">+{doc.tags.length - 3}</span>
              }
              </div>
            }
          </div>
        </div>
      </CardContent>
    </Card>);

};

// Upload Dialog Component
const UploadDialog = ({ open, onOpenChange, onUpload }) => {const { t } = useTranslation("wms");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    tags: '',
    entity_type: '',
    entity_id: '',
    warehouse_id: ''
  });
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (!formData.title) {
        setFormData((prev) => ({ ...prev, title: selectedFile.name.split('.')[0] }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!file || !formData.title || !formData.category) {
      toast.error('Please fill required fields and select a file');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const data = new FormData();
      data.append('file', file);
      data.append('title', formData.title);
      data.append('category', formData.category);
      if (formData.description) data.append('description', formData.description);
      if (formData.tags) data.append('tags', formData.tags);
      if (formData.entity_type) data.append('entity_type', formData.entity_type);
      if (formData.entity_id) data.append('entity_id', formData.entity_id);
      if (formData.warehouse_id) data.append('warehouse_id', formData.warehouse_id);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress((p) => Math.min(p + 10, 90));
      }, 200);

      await onUpload(data);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        onOpenChange(false);
        setFormData({ title: '', category: '', description: '', tags: '', entity_type: '', entity_id: '', warehouse_id: '' });
        setFile(null);
        setProgress(0);
      }, 500);
    } catch (error) {
      toast.error('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />{t("uploadDocument", "Upload Document")}

          </DialogTitle>
          <DialogDescription>{t("addANew", "Add a new document to the vault")}

          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Upload Area */}
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
              file ? 'border-emerald-500 bg-emerald-500/5' : 'border-muted-foreground/25 hover:border-primary/50'
            )}
            onClick={() => document.getElementById('file-upload').click()}>
            
            <input
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileChange}
              disabled={uploading} />
            
            {file ?
            <div className="flex items-center justify-center gap-3">
                <CheckCircle className="h-8 w-8 text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{formatFileSize(file.size)}</p>
                </div>
              </div> :

            <>
                <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">{t("clickToUpload", "Click to upload or drag and drop")}

              </p>
                <p className="text-xs text-muted-foreground mt-1">{t("pdfDocXls", "PDF, DOC, XLS, Images (max 50MB)")}

              </p>
              </>
            }
          </div>

          {uploading &&
          <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">{t("uploading", "Uploading...")}
              {progress}%
              </p>
            </div>
          }

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>{t("title", "Title *")}</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Document title"
                disabled={uploading} />
              
            </div>

            <div className="grid gap-2">
              <Label>{t("category", "Category *")}</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
                disabled={uploading}>
                
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([key, cat]) =>
                  <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <cat.icon className={cn('h-4 w-4', cat.color.split(' ')[0])} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>{t("description", "Description")}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
                disabled={uploading} />
              
            </div>

            <div className="grid gap-2">
              <Label>{t("tags", "Tags")}</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="Comma-separated tags (e.g. urgent, q4-2025)"
                disabled={uploading} />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>{t("entityType", "Entity Type")}</Label>
                <Select
                  value={formData.entity_type}
                  onValueChange={(v) => setFormData({ ...formData, entity_type: v })}
                  disabled={uploading}>
                  
                  <SelectTrigger>
                    <SelectValue placeholder="Link to..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order">{t("order", "Order")}</SelectItem>
                    <SelectItem value="shipment">{t("shipment", "Shipment")}</SelectItem>
                    <SelectItem value="route">{t("route", "Route")}</SelectItem>
                    <SelectItem value="customer">{t("customer", "Customer")}</SelectItem>
                    <SelectItem value="vendor">{t("vendor", "Vendor")}</SelectItem>
                    <SelectItem value="product">{t("product", "Product")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>{t("entityId", "Entity ID")}</Label>
                <Input
                  value={formData.entity_id}
                  onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                  placeholder="ORD-001"
                  disabled={uploading} />
                
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>{t("cancel", "Cancel")}

          </Button>
          <Button onClick={handleSubmit} disabled={uploading || !file || !formData.title || !formData.category}>
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Share Dialog Component
const ShareDialog = ({ open, onOpenChange, document, onShare }) => {const { t } = useTranslation("wms");
  const [shareData, setShareData] = useState({
    expires_hours: 24,
    can_download: true,
    can_print: false,
    max_downloads: '',
    password: ''
  });
  const [shareLink, setShareLink] = useState(null);

  const handleShare = async () => {
    try {
      const result = await onShare(document.document_id, shareData);
      setShareLink(result.share_url);
      toast.success('Share link created!');
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const copyToClipboard = () => {
    const fullUrl = `${window.location.origin}${shareLink}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard!');
  };

  return (
    <Dialog open={open} onOpenChange={(open) => {onOpenChange(open);if (!open) setShareLink(null);}}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />{t("shareDocument", "Share Document")}

          </DialogTitle>
          <DialogDescription>
            {document?.title}
          </DialogDescription>
        </DialogHeader>

        {shareLink ?
        <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <Label className="text-xs text-muted-foreground">{t("shareLink", "Share Link")}</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                value={`${window.location.origin}${shareLink}`}
                readOnly
                className="text-sm" />
              
                <Button size="icon" variant="outline" onClick={copyToClipboard}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setShareLink(null)}>{t("createAnotherLink", "Create Another Link")}

          </Button>
          </div> :

        <div className="space-y-4">
            <div className="grid gap-2">
              <Label>{t("expiresIn", "Expires In")}</Label>
              <Select
              value={String(shareData.expires_hours)}
              onValueChange={(v) => setShareData({ ...shareData, expires_hours: parseInt(v) })}>
              
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t("1Hour", "1 hour")}</SelectItem>
                  <SelectItem value="24">{t("24Hours", "24 hours")}</SelectItem>
                  <SelectItem value="168">{t("7Days", "7 days")}</SelectItem>
                  <SelectItem value="720">{t("30Days", "30 days")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label>{t("allowDownload", "Allow Download")}</Label>
              <input
              type="checkbox"
              checked={shareData.can_download}
              onChange={(e) => setShareData({ ...shareData, can_download: e.target.checked })}
              className="h-4 w-4" />
            
            </div>

            <div className="flex items-center justify-between">
              <Label>{t("allowPrint", "Allow Print")}</Label>
              <input
              type="checkbox"
              checked={shareData.can_print}
              onChange={(e) => setShareData({ ...shareData, can_print: e.target.checked })}
              className="h-4 w-4" />
            
            </div>

            <div className="grid gap-2">
              <Label>{t("maxDownloadsOptional", "Max Downloads (optional)")}</Label>
              <Input
              type="number"
              value={shareData.max_downloads}
              onChange={(e) => setShareData({ ...shareData, max_downloads: e.target.value })}
              placeholder="Unlimited" />
            
            </div>

            <div className="grid gap-2">
              <Label>{t("passwordProtectionOptional", "Password Protection (optional)")}</Label>
              <Input
              type="password"
              value={shareData.password}
              onChange={(e) => setShareData({ ...shareData, password: e.target.value })}
              placeholder="Leave empty for no password" />
            
            </div>
          </div>
        }

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {shareLink ? 'Close' : 'Cancel'}
          </Button>
          {!shareLink &&
          <Button onClick={handleShare}>
              <Link2 className="h-4 w-4 mr-2" />{t("createShareLink", "Create Share Link")}

          </Button>
          }
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

// Main Document Vault Component
export default function DocumentVault() {
  const { t } = useTranslation(['wms', 'common']);
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // grid | table

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);

      const token = getEzerpToken();
      const response = await fetch(`${API_URL}/api/documents/?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to fetch documents');
    }
  }, [token, categoryFilter, statusFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const token = getEzerpToken();
      const response = await fetch(`${API_URL}/api/documents/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDocuments(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDocuments, fetchStats]);

  const handleUpload = async (formData) => {
    const token = getEzerpToken();
    const response = await fetch(`${API_URL}/api/documents/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }

    toast.success('Document uploaded successfully!');
    fetchDocuments();
    fetchStats();
  };

  const handleShare = async (documentId, shareData) => {
    const token = getEzerpToken();
    const response = await fetch(`${API_URL}/api/documents/${documentId}/share`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(shareData)
    });

    if (!response.ok) {
      throw new Error('Share failed');
    }

    return await response.json();
  };

  const handleArchive = async (doc) => {
    if (!confirm(`Archive "${doc.title}"?`)) return;

    const token = getEzerpToken();
    try {
      const response = await fetch(`${API_URL}/api/documents/${doc.document_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Document archived');
        fetchDocuments();
        fetchStats();
      }
    } catch (error) {
      toast.error('Failed to archive document');
    }
  };

  const handleView = (doc) => {
    setSelectedDocument(doc);
    // TODO: Open detail view
    toast.info(`Viewing: ${doc.title}`);
  };

  const handleDownload = (doc) => {
    // TODO: Implement actual download
    toast.info(`Downloading: ${doc.file_name}`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="document-vault-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-7 w-7 text-primary" />
            {t('wms:documents.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('wms:documents.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => {fetchDocuments();fetchStats();}}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('common:refresh')}
          </Button>
          <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-document-btn">
            <Upload className="h-4 w-4 mr-2" />
            {t('wms:documents.upload')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats &&
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_documents || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('wms:documents.stats.totalDocuments')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_status?.active || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('common:active')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.uploaded_last_7_days || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('wms:documents.stats.thisWeek')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <FileSpreadsheet className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_category?.invoice || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('wms:documents.stats.invoices')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-500/10 flex items-center justify-center">
                  <Archive className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_status?.archived || 0}</p>
                  <p className="text-xs text-muted-foreground">{t('wms:documents.stats.archived')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('wms:documents.filters.searchPlaceholder')}
            className="pl-10"
            data-testid="search-documents-input" />
          
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('wms:documents.filters.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('wms:documents.filters.allCategories')}</SelectItem>
            {Object.entries(CATEGORIES).map(([key, cat]) =>
            <SelectItem key={key} value={key}>{cat.label}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('wms:documents.filters.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{t('wms:documents.status.active')}</SelectItem>
            <SelectItem value="archived">{t('wms:documents.status.archived')}</SelectItem>
            <SelectItem value="draft">{t('wms:documents.status.draft')}</SelectItem>
            <SelectItem value="expired">{t('wms:documents.status.expired')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Document List */}
      {loading ?
      <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div> :
      documents.length === 0 ?
      <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">{t('wms:documents.empty.title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('wms:documents.empty.subtitle')}
            </p>
            <Button onClick={() => setUploadDialogOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              {t('wms:documents.upload')}
            </Button>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc) =>
        <DocumentCard
          key={doc.id}
          doc={doc}
          onView={handleView}
          onDownload={handleDownload}
          onShare={(d) => {setSelectedDocument(d);setShareDialogOpen(true);}}
          onArchive={handleArchive} />

        )}
        </div>
      }

      {/* Dialogs */}
      <UploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUpload={handleUpload} />
      
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        document={selectedDocument}
        onShare={handleShare} />
      
    </div>);

}
